/**
 * Consolidated Formatting Utilities
 * Single source of truth for phone, date, currency, and status formatting
 */

// ============================================================
// PHONE FORMATTING
// ============================================================

/**
 * Normalize phone number to international format (UK focused)
 * @param {string} phone - Phone number in any format
 * @returns {string|null} Normalized phone number (e.g., "447700900456") or null
 */
export function normalizePhone(phone) {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  if (!digits) return null;
  
  // UK number starting with 0 -> add 44
  if (digits.startsWith('0') && digits.length >= 10) {
    return '44' + digits.substring(1);
  }
  
  // Already has country code
  if (digits.startsWith('44')) {
    return digits;
  }
  
  // Assume UK and add 44
  return '44' + digits;
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone (e.g., "+44 7700 900456")
 */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';
  
  const normalized = normalizePhone(phone);
  if (!normalized) return phone;
  
  // Format as +44 XXXX XXXXXX
  if (normalized.startsWith('44') && normalized.length >= 12) {
    return `+${normalized.slice(0, 2)} ${normalized.slice(2, 6)} ${normalized.slice(6)}`;
  }
  
  return phone;
}

/**
 * Build WhatsApp link with pre-filled message
 * @param {string} phone - Phone number
 * @param {string} message - Pre-filled message (optional)
 * @returns {string|null} WhatsApp URL or null
 */
export function buildWhatsAppLink(phone, message = '') {
  const normalized = normalizePhone(phone);
  if (!normalized) return null;
  
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${normalized}${message ? `?text=${encodedMessage}` : ''}`;
}

/**
 * Build tel: link for phone calls
 * @param {string} phone - Phone number
 * @returns {string|null} tel: URL or null
 */
export function buildTelLink(phone) {
  if (!phone) return null;
  return `tel:${phone.replace(/\s/g, '')}`;
}

// ============================================================
// DATE/TIME FORMATTING
// ============================================================

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} style - 'short', 'medium', 'long', 'relative'
 * @returns {string} Formatted date
 */
export function formatDate(date, style = 'medium') {
  if (!date) return '-';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  
  const now = new Date();
  
  switch (style) {
    case 'short':
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    
    case 'long':
      return d.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    
    case 'relative':
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    
    case 'datetime':
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case 'time':
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    
    case 'medium':
    default:
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}

/**
 * Format timestamp for audit logs
 * @param {string|Date} timestamp - Timestamp
 * @returns {string} ISO formatted timestamp with readable version
 */
export function formatAuditTimestamp(timestamp) {
  if (!timestamp) return '-';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '-';
  
  return d.toISOString();
}

/**
 * Calculate SLA status
 * @param {object} job - Job object with sla_due_date or target_date
 * @returns {object} { status: 'on_track'|'at_risk'|'overdue', daysRemaining, label }
 */
export function calculateSLAStatus(job) {
  const dueDate = job?.sla_due_date || job?.target_date;
  if (!dueDate) return { status: 'unknown', daysRemaining: null, label: 'No SLA' };
  
  const now = new Date();
  const due = new Date(dueDate);
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / 86400000);
  
  if (diffDays < 0) {
    return { status: 'overdue', daysRemaining: diffDays, label: `${Math.abs(diffDays)}d overdue` };
  }
  if (diffDays <= 2) {
    return { status: 'at_risk', daysRemaining: diffDays, label: `${diffDays}d left` };
  }
  return { status: 'on_track', daysRemaining: diffDays, label: `${diffDays}d left` };
}

// ============================================================
// CURRENCY FORMATTING
// ============================================================

/**
 * Format currency value
 * @param {number} value - Amount
 * @param {string} currency - Currency code (default: GBP)
 * @param {boolean} showSymbol - Whether to show currency symbol
 * @returns {string} Formatted currency
 */
export function formatCurrency(value, currency = 'GBP', showSymbol = true) {
  if (value === null || value === undefined) return '-';
  if (isNaN(value)) return '-';
  
  const formatter = new Intl.NumberFormat('en-GB', {
    style: showSymbol ? 'currency' : 'decimal',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  
  return formatter.format(value);
}

/**
 * Format currency range
 * @param {number} low - Low value
 * @param {number} high - High value
 * @returns {string} Formatted range (e.g., "£500 - £1,200")
 */
export function formatCurrencyRange(low, high) {
  if (low === null || high === null) return '-';
  return `${formatCurrency(low)} - ${formatCurrency(high)}`;
}

// ============================================================
// STATUS FORMATTING
// ============================================================

/**
 * Normalize job status string
 * @param {string} status - Raw status string
 * @returns {string} Normalized status key (lowercase, underscored)
 */
export function normalizeStatus(status) {
  if (!status) return 'unknown';
  return status.toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Get status display configuration
 * @param {string} status - Job status
 * @returns {object} { label, color, bgColor, textColor, icon }
 */
export function getStatusConfig(status) {
  const normalized = normalizeStatus(status);
  
  const configs = {
    new: { label: 'New', color: 'bg-blue-500', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    assigned: { label: 'Assigned', color: 'bg-purple-500', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
    awaiting_quote: { label: 'Awaiting Quote', color: 'bg-amber-500', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
    quote_submitted: { label: 'Quote Submitted', color: 'bg-teal-500', bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
    quote_approved: { label: 'Quote Approved', color: 'bg-emerald-500', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    approved: { label: 'Approved', color: 'bg-emerald-500', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    in_progress: { label: 'In Progress', color: 'bg-blue-600', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    completed: { label: 'Completed', color: 'bg-green-500', bgColor: 'bg-green-100', textColor: 'text-green-700' },
    awaiting_invoice: { label: 'Awaiting Invoice', color: 'bg-orange-500', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
    invoiced: { label: 'Invoiced', color: 'bg-indigo-500', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
    paid: { label: 'Paid', color: 'bg-emerald-600', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    closed: { label: 'Closed', color: 'bg-slate-500', bgColor: 'bg-slate-100', textColor: 'text-slate-700' },
    cancelled: { label: 'Cancelled', color: 'bg-red-500', bgColor: 'bg-red-100', textColor: 'text-red-700' }
  };
  
  return configs[normalized] || { 
    label: status || 'Unknown', 
    color: 'bg-slate-400', 
    bgColor: 'bg-slate-100', 
    textColor: 'text-slate-600' 
  };
}

/**
 * Get priority configuration
 * @param {string} priority - Priority level
 * @returns {object} { label, color, bgColor, textColor }
 */
export function getPriorityConfig(priority) {
  const normalized = (priority || '').toLowerCase();
  
  const configs = {
    emergency: { label: 'Emergency', color: 'bg-red-600', bgColor: 'bg-red-100', textColor: 'text-red-700' },
    urgent: { label: 'Urgent', color: 'bg-orange-500', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
    high: { label: 'High', color: 'bg-amber-500', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
    medium: { label: 'Medium', color: 'bg-blue-500', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    low: { label: 'Low', color: 'bg-slate-400', bgColor: 'bg-slate-100', textColor: 'text-slate-600' }
  };
  
  return configs[normalized] || configs.medium;
}

// ============================================================
// EVIDENCE FORMATTING
// ============================================================

/**
 * Get evidence category label
 * @param {string} category - Evidence category key
 * @returns {string} Human readable label
 */
export function getEvidenceCategoryLabel(category) {
  const labels = {
    before: 'Before Photos',
    during: 'During Photos',
    after: 'After Photos',
    sat_note: 'SAT Note',
    certificate: 'Certificate',
    invoice: 'Invoice',
    quote: 'Quote',
    access_notes: 'Access Notes',
    other: 'Other'
  };
  return labels[category] || category;
}

/**
 * Get evidence review status configuration
 * @param {string} status - Review status
 * @returns {object} { label, color, bgColor, textColor }
 */
export function getEvidenceReviewConfig(status) {
  const configs = {
    approved: { label: 'Approved', color: 'bg-emerald-500', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    rejected: { label: 'Rejected', color: 'bg-red-500', bgColor: 'bg-red-100', textColor: 'text-red-700' },
    more_requested: { label: 'More Requested', color: 'bg-amber-500', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
    pending: { label: 'Pending Review', color: 'bg-blue-500', bgColor: 'bg-blue-100', textColor: 'text-blue-700' }
  };
  
  return configs[status] || { label: status || 'Unknown', color: 'bg-slate-400', bgColor: 'bg-slate-100', textColor: 'text-slate-600' };
}

// ============================================================
// AUDIT EVENT FORMATTING
// ============================================================

/**
 * Get audit event icon and color
 * @param {string} eventType - Event type
 * @returns {object} { icon, color, label }
 */
export function getAuditEventConfig(eventType) {
  const configs = {
    status_change: { color: 'bg-blue-500', label: 'Status Change' },
    evidence_uploaded: { color: 'bg-teal-500', label: 'Evidence Uploaded' },
    evidence_deleted: { color: 'bg-red-400', label: 'Evidence Deleted' },
    evidence_review: { color: 'bg-purple-500', label: 'Evidence Reviewed' },
    workflow_enforcement: { color: 'bg-amber-500', label: 'Workflow Enforcement' },
    communication: { color: 'bg-slate-500', label: 'Communication' },
    assignment: { color: 'bg-indigo-500', label: 'Assignment' },
    quote_submitted: { color: 'bg-orange-500', label: 'Quote Submitted' },
    invoice_submitted: { color: 'bg-green-500', label: 'Invoice Submitted' },
    payment: { color: 'bg-emerald-600', label: 'Payment' },
    escalation: { color: 'bg-red-500', label: 'Escalation' },
    system: { color: 'bg-slate-400', label: 'System' }
  };
  
  return configs[eventType] || { color: 'bg-slate-400', label: eventType || 'Event' };
}

export default {
  normalizePhone,
  formatPhoneDisplay,
  buildWhatsAppLink,
  buildTelLink,
  formatDate,
  formatAuditTimestamp,
  calculateSLAStatus,
  formatCurrency,
  formatCurrencyRange,
  normalizeStatus,
  getStatusConfig,
  getPriorityConfig,
  getEvidenceCategoryLabel,
  getEvidenceReviewConfig,
  getAuditEventConfig
};
